import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import PageMeta from "../components/PageMeta";
import { fetchPublishedStories } from "../lib/api";

function StoryCard({ s }) {
  return (
    <article
      data-testid={`story-${s.id}-card`}
      className="border border-rule bg-linen p-8 md:p-10 flex flex-col hover:-translate-y-1 hover:border-rule-strong transition-all duration-300"
    >
      <div className="text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold">
        {s.role || "Anonymous"}
      </div>
      <h2 className="font-serif text-2xl md:text-[1.7rem] mt-4 text-ink leading-tight tracking-tight">
        {s.title}
      </h2>
      <p className="mt-5 text-ink-muted leading-relaxed flex-1 italic font-light whitespace-pre-wrap">
        &ldquo;{s.story}&rdquo;
      </p>
      <div className="mt-6 pt-6 border-t border-rule text-sm font-accent text-ink-muted">
        — {s.pen_name || "Anonymous"}
      </div>
    </article>
  );
}

export default function StoriesPage() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPublishedStories()
      .then(setStories)
      .catch(() => setStories([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="stories-page">
      <PageMeta
        title="Stories"
        description="Anonymous accounts shared by families who have lived through the child protection and family court system. Each was shared with permission."
        path="/stories"
      />
      <PageHeader
        overline="Stories"
        title="In their own words."
        lede="Accounts shared by families who have lived through the child protection and family court system. Names have been changed. Each was shared with permission."
      />

      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20 md:py-24">
        {loading ? (
          <p data-testid="stories-loading" className="text-ink-muted font-accent">Loading…</p>
        ) : stories.length === 0 ? (
          <div className="border border-rule bg-linen-alt p-10 text-center text-ink-muted">
            No stories have been published yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10" data-testid="stories-list">
            {stories.map((s) => (
              <StoryCard key={s.id} s={s} />
            ))}
          </div>
        )}

        <div className="mt-20 border border-rule bg-linen-alt p-10 md:p-14 max-w-3xl mx-auto text-center">
          <div className="text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold">
            Share your story
          </div>
          <h3 className="font-serif text-3xl md:text-4xl mt-4 text-ink leading-tight tracking-tight text-balance">
            Your experience could be the one another family needs to read.
          </h3>
          <p className="mt-5 text-ink-muted leading-relaxed">
            Anonymity is respected as standard. Nothing is published without your explicit consent.
          </p>
          <Link
            to="/stories/share"
            data-testid="stories-share-cta"
            className="mt-8 inline-flex items-center gap-2 bg-river text-linen px-6 py-3 text-sm font-accent font-semibold tracking-wide hover:bg-river-dark transition-colors"
          >
            Share your story
          </Link>
        </div>
      </section>
    </div>
  );
}
